/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * offlineDb.js — Livello dati LOCAL-FIRST basato su IndexedDB (nativo, zero
 * dipendenze). Garantisce che i flussi clinici essenziali funzionino al 100%
 * offline; la sincronizzazione con il server (MongoDB o PostgreSQL/Supabase
 * tramite `DATABASE_URL`) avviene in `syncEngine.js` appena torna la rete.
 *
 * Store:
 *   medical_records  — cartelle cliniche SOAP (chiave: id UUID)
 *   voice_notes      — note del diario vocale OSS/infermieri
 *   vitals           — parametri vitali rilevati dall'OSS (PA, FC, T°, SpO2, glicemia…)
 *   consents         — consensi informati FEA (PDF cifrato AES-GCM, vedi Fase 3)
 *   documents        — referti cifrati E2EE per il portale paziente (Fase 4)
 *   outbox           — coda operazioni da sincronizzare (idempotenti)
 *   meta             — chiavi/valori (ultimo sync, versione schema)
 *
 * Ogni record ha: id, created_at, updated_at, sync_status ∈ 'pending'|'synced'|'error'.
 *
 * Ciclo local-first (modulo OSS): salva SEMPRE prima in IndexedDB (mai errore per
 * assenza di rete) → accoda in `outbox` → `syncEngine` ascolta l'evento `online` e
 * spinge i record al server (DB centrale) → con `purge_local: true` il record locale
 * viene eliminato dopo la conferma del server (memoria locale svuotata).
 */

const DB_NAME = 'sanitanexus_local';
const DB_VERSION = 3;

export const STORES = Object.freeze({
  MEDICAL_RECORDS: 'medical_records',
  VOICE_NOTES: 'voice_notes',
  VITALS: 'vitals',
  CONSENTS: 'consents',
  DOCUMENTS: 'documents',
  OUTBOX: 'outbox',
  META: 'meta',
});

let dbPromise = null;

export const isIndexedDbAvailable = () => typeof indexedDB !== 'undefined';

/** UUID v4 (crypto-safe con fallback). */
export const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const nowIso = () => new Date().toISOString();

/** Apre (o crea/migra) il database. Una sola connessione condivisa. */
export const openDb = () => {
  if (!isIndexedDbAvailable()) return Promise.reject(new Error('IndexedDB non disponibile'));
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (ev) => {
      const db = req.result;
      const ensure = (name, indexes = []) => {
        const store = db.objectStoreNames.contains(name)
          ? ev.target.transaction.objectStore(name)
          : db.createObjectStore(name, { keyPath: 'id' });
        indexes.forEach(([idx, keyPath, opts]) => {
          if (!store.indexNames.contains(idx)) store.createIndex(idx, keyPath, opts || {});
        });
      };
      ensure(STORES.MEDICAL_RECORDS, [['by_patient', 'patient_id'], ['by_appt', 'appointment_id'], ['by_sync', 'sync_status'], ['by_updated', 'updated_at']]);
      ensure(STORES.VOICE_NOTES, [['by_patient', 'patient_id'], ['by_sync', 'sync_status'], ['by_updated', 'updated_at']]);
      ensure(STORES.VITALS, [['by_patient', 'patient_id'], ['by_sync', 'sync_status'], ['by_updated', 'updated_at']]);
      ensure(STORES.CONSENTS, [['by_patient', 'patient_id'], ['by_sync', 'sync_status']]);
      ensure(STORES.DOCUMENTS, [['by_patient', 'patient_id'], ['by_sync', 'sync_status']]);
      ensure(STORES.OUTBOX, [['by_created', 'created_at']]);
      ensure(STORES.META);
    };
    req.onsuccess = () => {
      const db = req.result;
      // Multi-tab: se un altro tab aggiorna lo schema, chiudiamo questa connessione
      // (altrimenti bloccheremmo l'upgrade) e la riapriremo pigramente alla prossima op.
      db.onversionchange = () => { try { db.close(); } catch (e) { /* ignore */ } dbPromise = null; };
      db.onclose = () => { dbPromise = null; };
      resolve(db);
    };
    req.onerror = () => { dbPromise = null; reject(req.error); };
    // `blocked` NON è fatale: l'upgrade riparte appena l'altro tab chiude la connessione
    // e `onsuccess` verrà comunque invocato. Non rifiutiamo (eviterebbe ogni scrittura futura).
    req.onblocked = () => { if (typeof console !== 'undefined') console.warn('[offlineDb] upgrade IndexedDB in attesa: chiudi gli altri tab dell\'app'); };
  });
  return dbPromise;
};

const tx = async (store, mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    let out;
    try { out = fn(s); } catch (e) { reject(e); return; }
    t.oncomplete = () => resolve(out && 'result' in out ? out.result : out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('transaction aborted'));
  });
};

// ---------------------------------------------------------------------------
// Generic CRUD
// ---------------------------------------------------------------------------

export const putRecord = (store, record) => tx(store, 'readwrite', (s) => s.put(record));
export const getRecord = (store, id) => tx(store, 'readonly', (s) => s.get(id));
export const deleteRecord = (store, id) => tx(store, 'readwrite', (s) => s.delete(id));
export const getAll = (store) => tx(store, 'readonly', (s) => s.getAll());
export const clearStore = (store) => tx(store, 'readwrite', (s) => s.clear());
export const countRecords = (store) => tx(store, 'readonly', (s) => s.count());

export const getAllByIndex = async (store, index, value) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, 'readonly');
    const req = t.objectStore(store).index(index).getAll(value);
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
};

// ---------------------------------------------------------------------------
// Meta (key/value)
// ---------------------------------------------------------------------------

export const setMeta = (key, value) => putRecord(STORES.META, { id: key, value, updated_at: nowIso() });
export const getMeta = async (key) => (await getRecord(STORES.META, key))?.value;

// ---------------------------------------------------------------------------
// Outbox — coda operazioni idempotenti da inviare al server
// ---------------------------------------------------------------------------

/**
 * @param {{ kind: string, method?: string, path: string, body?: any, ref_store?: string, ref_id?: string }} op
 */
export const queueOutbox = (op) => putRecord(STORES.OUTBOX, {
  id: uuid(),
  created_at: nowIso(),
  attempts: 0,
  last_error: null,
  method: 'POST',
  ...op,
});

export const getPendingOutbox = async () => {
  const items = await getAll(STORES.OUTBOX);
  return items.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
};

export const removeOutbox = (id) => deleteRecord(STORES.OUTBOX, id);

export const markOutboxError = async (id, error) => {
  const item = await getRecord(STORES.OUTBOX, id);
  if (!item) return;
  await putRecord(STORES.OUTBOX, { ...item, attempts: (item.attempts || 0) + 1, last_error: String(error?.message || error) });
};

// ---------------------------------------------------------------------------
// Domain helpers — Medical records (SOAP)
// ---------------------------------------------------------------------------

/**
 * Salva ISTANTANEAMENTE una cartella clinica in locale e accoda la sync.
 * Non lancia mai verso il chiamante per problemi di rete: è puro storage.
 *
 * @param {{ id?: string, appointment_id?: string, patient_id?: string, patient_name?: string,
 *           soap: { subjective, objective, assessment, plan }, transcript?: string,
 *           ai_provider?: string, author_id?: string }} record
 * @returns {Promise<object>} il record salvato (con id, timestamps)
 */
export const saveMedicalRecordOffline = async (record) => {
  const now = nowIso();
  const doc = {
    id: record.id || uuid(),
    created_at: record.created_at || now,
    updated_at: now,
    sync_status: 'pending',
    ...record,
  };
  await putRecord(STORES.MEDICAL_RECORDS, doc);
  if (doc.appointment_id) {
    await queueOutbox({
      kind: 'clinical_note',
      path: `/telemedicine/appointments/${doc.appointment_id}/notes`,
      body: {
        notes: formatRecordAsNotes(doc),
        diagnosis: doc.soap?.assessment || null,
        prescriptions: doc.soap?.plan || null,
      },
      ref_store: STORES.MEDICAL_RECORDS,
      ref_id: doc.id,
    });
  }
  return doc;
};

export const listMedicalRecordsOffline = async ({ appointment_id, patient_id } = {}) => {
  let items;
  if (appointment_id) items = await getAllByIndex(STORES.MEDICAL_RECORDS, 'by_appt', appointment_id);
  else if (patient_id) items = await getAllByIndex(STORES.MEDICAL_RECORDS, 'by_patient', patient_id);
  else items = await getAll(STORES.MEDICAL_RECORDS);
  return items.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
};

export const markRecordSynced = async (store, id, serverId) => {
  const item = await getRecord(store, id);
  if (!item) return;
  await putRecord(store, { ...item, sync_status: 'synced', server_id: serverId || item.server_id || null, synced_at: nowIso() });
};

export const formatRecordAsNotes = (rec) => {
  const s = rec.soap || {};
  const parts = [];
  if (s.subjective) parts.push(`S: ${s.subjective}`);
  if (s.objective) parts.push(`O: ${s.objective}`);
  if (s.assessment) parts.push(`A: ${s.assessment}`);
  if (s.plan) parts.push(`P: ${s.plan}`);
  if (rec.ai_provider) parts.push(`\n[AI locale: ${rec.ai_provider}]`);
  return parts.join('\n');
};

// ---------------------------------------------------------------------------
// Domain helpers — Voice notes (OSS)
// ---------------------------------------------------------------------------

export const saveVoiceNoteOffline = async (note) => {
  const now = nowIso();
  const doc = { id: note.id || uuid(), created_at: note.created_at || now, updated_at: now, sync_status: 'pending', ...note };
  await putRecord(STORES.VOICE_NOTES, doc);
  await queueOutbox({
    kind: 'voice_note',
    path: '/voice-diary/save',
    body: {
      transcript: doc.transcript,
      structured: doc.structured,
      patient_id: doc.patient_id || null,
      patient_label: doc.patient_label || null,
      tags: doc.tags || [],
    },
    ref_store: STORES.VOICE_NOTES,
    ref_id: doc.id,
  });
  return doc;
};

export const listVoiceNotesOffline = async () => {
  const items = await getAll(STORES.VOICE_NOTES);
  return items.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
};

// ---------------------------------------------------------------------------
// Domain helpers — Parametri vitali (OSS, local-first)
// ---------------------------------------------------------------------------

/**
 * Salva ISTANTANEAMENTE i parametri vitali in locale (anche senza rete) e accoda
 * il push verso `/voice-diary/vitals`. Il `client_id` (= id locale) rende
 * l'invio idempotente; dopo la conferma del server il record locale viene
 * eliminato (`purge_local`), così la memoria del dispositivo resta pulita.
 *
 * @param {{ patient_id?: string, patient_label?: string, values: object, note?: string, measured_at?: string }} vitals
 */
export const saveVitalsOffline = async (vitals) => {
  const now = nowIso();
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const doc = { id: vitals.id || uuid(), created_at: now, updated_at: now, sync_status: 'pending', offline, ...vitals, measured_at: vitals.measured_at || now };
  await putRecord(STORES.VITALS, doc);
  await queueOutbox({
    kind: 'vitals',
    path: '/voice-diary/vitals',
    body: {
      client_id: doc.id,
      patient_id: doc.patient_id || null,
      patient_label: doc.patient_label || null,
      measured_at: doc.measured_at,
      values: doc.values || {},
      note: doc.note || null,
      offline,
    },
    ref_store: STORES.VITALS,
    ref_id: doc.id,
    purge_local: true,
  });
  return doc;
};

export const listVitalsOffline = async () => {
  const items = await getAll(STORES.VITALS);
  return items.sort((a, b) => (a.measured_at < b.measured_at ? 1 : -1));
};

/** Rimuove dallo store i record già sincronizzati (housekeeping memoria locale). */
export const purgeSyncedRecords = async (store) => {
  const items = await getAll(store);
  let n = 0;
  for (const it of items) {
    if (it.sync_status === 'synced') { await deleteRecord(store, it.id); n += 1; }
  }
  return n;
};

// ---------------------------------------------------------------------------
// Stats (per indicatori UI)
// ---------------------------------------------------------------------------

export const OUTBOX_MAX_ATTEMPTS = 8;

export const getOfflineStats = async () => {
  const empty = { available: false, pending: 0, failed: 0, records: 0, voiceNotes: 0, vitals: 0 };
  if (!isIndexedDbAvailable()) return empty;
  try {
    const [outbox, records, voiceNotes, vitals] = await Promise.all([
      getAll(STORES.OUTBOX), countRecords(STORES.MEDICAL_RECORDS), countRecords(STORES.VOICE_NOTES), countRecords(STORES.VITALS),
    ]);
    const failed = outbox.filter((o) => (o.attempts || 0) >= OUTBOX_MAX_ATTEMPTS).length;
    return { available: true, pending: outbox.length - failed, failed, records, voiceNotes, vitals };
  } catch (e) {
    return empty;
  }
};

/** Riporta a zero i tentativi delle operazioni fallite (riprova manuale dall'UI). */
export const retryFailedOutbox = async () => {
  const items = await getAll(STORES.OUTBOX);
  let n = 0;
  for (const it of items) {
    if ((it.attempts || 0) >= OUTBOX_MAX_ATTEMPTS) { await putRecord(STORES.OUTBOX, { ...it, attempts: 0 }); n += 1; }
  }
  return n;
};
